import _ from 'lodash';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import styled from 'styled-components';

import { Spin, Tabs, Affix } from 'antd';

import { customFleets } from 'shared';
import { showDemoVinOrRealVin } from 'shared/utils';

import MapIcon from 'assets/icons/map.png';

import { AppStore, CarStore, ShopStore } from 'stores';
import { CarObject } from 'stores/Classes';

import { AnalyticsView } from 'containers/AnalyticsView';
import AssetWorkOrders from 'containers/TrailerProfile/AssetWorkOrders';
import CarAppointments from './CarAppointments';
import CarDocumentSections from './CarDocumentSections';
import CarIssuesPage from './CarIssuesPage';
import CarProfileHeader from './CarProfileHeader';
import CarReportsSection from './CarReportsSection';
import CarTripsView from './CarTripsView';
import Inspections from 'containers/InspectionForms/Inspections';

const VehicleHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
  margin-bottom: 12px;
  > p {
    font-size: 14px;
    margin: 0 20px;
  }
  > p > strong {
    font-size: 20px;
  }
`;

const ModernStickyContainer = ({ children, ...props }) => {
  return <div {...props}>{children}</div>;
};

const CustomTabBar = (props) => {
  return (
    <Affix offsetTop={64}>
      <div style={{ background: '#fff', zIndex: 4, top: '64px' }}>
        {props.children}
      </div>
    </Affix>
  );
};

const renderTabBar = (props, DefaultTabBar) => (
  <CustomTabBar>
    <DefaultTabBar
      {...props}
      style={{
        zIndex: 4,
        background: '#fff',
        top: '64px',
      }}
    />
  </CustomTabBar>
);

const CarProfile = ({ history, match }) => {
  const [activeKey, setActiveKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const carId = Number(match.params.id);
  const { selector } = match.params;

  const car = CarStore.data.get(carId);

  const shopId = useMemo(() => {
    return ShopStore.currentShop.id === -1
      ? car?.shopId
      : ShopStore.currentShop.id;
  }, [car?.shopId]);

  useEffect(() => {
    const initializeComponent = async () => {
      AppStore.setSelectedSidebarKey('/vehicles');

      setIsLoading(true);

      if (!CarStore.data.has(carId)) {
        await CarStore.getCarById(match.params.id);
      }

      const carData = await CarStore.getCarByIdAndReturn(match.params.id);
      CarStore.setCarDataKey(carId, 'tags', carData?.tags || '');

      setIsLoading(false);
    };

    initializeComponent();
  }, [match.params.id, carId]);

  useEffect(() => {
    let initialActiveKey;
    if (selector) {
      initialActiveKey = selector.toNormalString().capitalizeFirstLetter();
    } else if (car) {
      initialActiveKey =
        car.carType?.toLowerCase() === 'trailer' ? 'General' : 'Service';
    }
    setActiveKey(initialActiveKey);
  }, [selector, car]);

  const changeTab = (newActiveKey) => {
    history.replace(`/car/${match.params.id}/${newActiveKey.toReadableUrl()}`);
    setActiveKey(newActiveKey);
  };

  const getTabs = useMemo(() => {
    if (!car) return [];

    let leanDashboardPanels = [
      {
        key: 'General',
        label: 'General',
        children: <CarProfileHeader car={car} shopId={shopId} />,
      },
    ];

    const isGeorgiaPower = customFleets.georgiapower.includes(
      ShopStore.currentShop.id
    );
    const isTimHortons = customFleets.timhortons.includes(
      ShopStore.currentShop.id
    );
    const isExcludedShop = isGeorgiaPower || isTimHortons;

    if (
      !isExcludedShop &&
      !customFleets.motiveFleets.includes(ShopStore.currentShop.id)
    ) {
      leanDashboardPanels.push({
        key: 'Trips',
        label: 'Trips',
        children: <CarTripsView car={car} shopId={shopId} />,
      });
    }

    const vehicleSpecificTabs = [
      {
        key: 'Service',
        label: 'Service',
        children: <CarIssuesPage car={car} />,
      },
      {
        key: 'Analytics',
        label: 'Analytics',
        children: <AnalyticsView car={car} />,
      },
      {
        key: 'Pitstop Insights',
        label: 'Pitstop Insights',
        children: <CarReportsSection car={car} />,
      },
      {
        key: 'Appointments',
        label: 'Appointments',
        children: <CarAppointments car={car} />,
      },
      {
        key: 'Inspection Forms',
        label: 'Inspection Forms',
        children: <Inspections />,
      },
    ];

    const visibleTabs =
      car.carType?.toLowerCase() === 'vehicle' ? vehicleSpecificTabs : [];

    leanDashboardPanels = [
      ...leanDashboardPanels,
      ...visibleTabs,
      {
        key: 'Work Orders',
        label: 'Work Orders',
        children: <AssetWorkOrders car={car} shopId={shopId} />,
      },
      {
        key: 'Documents',
        label: 'Documents',
        children: <CarDocumentSections car={car} />,
      },
    ];

    let tabs = leanDashboardPanels;

    if (customFleets.gmrv.includes(shopId)) {
      const customTabs = ['General', 'Trips', 'Service', 'Pitstop Insights'];
      tabs = leanDashboardPanels.filter((tab) => customTabs.includes(tab.key));
    } else if (customFleets.bmc.includes(shopId)) {
      const customTabs = ['General', 'Trips', 'Service', 'Appointments'];
      tabs = leanDashboardPanels.filter((tab) => customTabs.includes(tab.key));
    } else if (customFleets.wolfline.includes(shopId)) {
      const customTabs = ['General', 'Service', 'Appointments'];
      tabs = leanDashboardPanels.filter((tab) => customTabs.includes(tab.key));
    }

    return tabs;
  }, [car, shopId]);

  const vinFrag = useMemo(() => {
    if (!car || !car.vin) return null;
    return <p>{`VIN: ${showDemoVinOrRealVin(car.vin)}`}</p>;
  }, [car]);

  const carNameFrag = useMemo(() => {
    if (!car || !car.carName) return null;
    return <p>{`Unit Id: ${car.carName}`}</p>;
  }, [car]);

  if (isLoading || !car) {
    return <Spin size="large" />;
  }

  return (
    <div
      style={{
        maxHeight: '100%',
        minWidth: '77%',
        marginTop: 10,
      }}
    >
      <VehicleHeaderContainer>
        <p style={{ fontSize: '20px' }}>
          <strong>{`${car.mmy}`}</strong>
        </p>
        {!customFleets.motiveFleets.includes(ShopStore.currentShop.id) && (
          <Link to={`/location?car=${match.params.id}`}>
            <img src={MapIcon} style={{ height: '4vh' }} alt="Live Location" />
          </Link>
        )}
        {vinFrag}
        {carNameFrag}
      </VehicleHeaderContainer>

      <ModernStickyContainer>
        <Tabs
          defaultActiveKey={activeKey}
          activeKey={activeKey}
          size="large"
          onChange={changeTab}
          items={getTabs}
          renderTabBar={renderTabBar}
        />
      </ModernStickyContainer>
    </div>
  );
};

CarProfile.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  car: PropTypes.instanceOf(CarObject),
};

export default withRouter(observer(CarProfile));
